<script lang="ts">
import type { AcceptableValue } from '@nuxt/ui';
import type { SelectFieldOptions } from '#layers/block-engine/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { SelectMenuEmits, SelectMenuProps } from '#layers/ui/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { defineBlock, selectOptionsFormSchema, useSelectOptions } from '#layers/block-engine/public';

interface InputSelectBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<SelectMenuProps, 'size' | 'variant' | 'icon' | 'createItem' | 'placeholder' | 'ui' | 'trailingIcon' | 'open' | 'class' | 'modelModifiers'> {
	/**
	 * Convert the value to title case when no explicit label is provided
	 *
	 * @default true
	 */
	autoTitleCaseValue?: boolean;
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_select.name',
	description: '$t:blocks.input_select.description',
	icon: 'lucide:list-chevrons-up-down',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ['select'] },
	events: [
		{
			name: 'clear',
			payload: [],
		},
		{
			name: 'update:open',
			payload: [
				{
					name: 'value',
					type: 'boolean',
					required: true,
					array: false,
				},
			],
		},
	],

	options: ({ form, values }) => form([
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		selectOptionsFormSchema({ options: values.options }),
		{
			name: 'multiple',
			label: '$t:blocks.common.props.multiple',
		},
		{
			name: 'autoTitleCaseValue',
			label: '$t:blocks.common.props.auto_title_case_value',
		},
		{
			name: 'placeholder',
			label: '$t:blocks.common.props.placeholder',
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'icon',
			label: '$t:blocks.common.props.icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'modelModifiers',
			hidden: true,
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	]),
}</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, useSlots } from 'vue';
import { useAppConfig } from '#app/config';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { OptionSelect, useDisabledNotice, useFormField } from '#layers/ui/public';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(defineProps<InputSelectBlockProps>(), {
	autoTitleCaseValue: true,
	placeholder: '$t:common.select_value',
});
const emits = defineEmits<SelectMenuEmits<any, any, any>>();
const model = defineModel<any>();
const slots = useSlots();

;

const appConfig = useAppConfig();
const forwarded = useForwardPropsEmits(reactivePick(props, 'size', 'variant', 'icon', 'ui', 'open'), emits);
const { modelValue, disabled, disabledNotice, nullable } = usePrimitiveFormField(props, { model });
const { normalizedOptions: items } = useSelectOptions(() => props.options);
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const { translateString } = useTranslationUtils();
const placeholder = computed(() => props.placeholder ? translateString(props.placeholder) : undefined);
const trailingIcon = computed(() => {
	if (props.trailingIcon !== undefined) return props.trailingIcon;
	if (props.readonly) return '';
	return props.multiple ? 'lucide:plus' : undefined;
});
const { emitFormChange, emitFormInput } = useFormField<InputSelectBlockProps>(props, { forwardContext: true });

function removeItem(value: AcceptableValue) {
	if (!modelValue.value) return;

	modelValue.value = (modelValue.value as Array<AcceptableValue>).filter((item) => item !== value) as Array<AcceptableValue>;
	emitFormChange();
	emitFormInput();
}
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<!-- TODO: <div> necessary because DTooltip doesn't play nice with SelectMenu -->
		<div>
			<OptionSelect
				v-bind="{ ...forwarded, ...$attrs }"
				v-model="modelValue"
				:model-modifiers="modelModifiers ?? {}"
				:disabled
				:readonly
				:items
				:multiple
				:placeholder
				:clear="nullable"
				:create-item
				:remove-icon="appConfig.ui.icons.remove"
				:trailing-icon
				@remove-item="removeItem"
			>
				<template v-if="slots.default" #default="slotProps">
					<slot v-bind="slotProps" />
				</template>
			</OptionSelect>
		</div>
	</DTooltip>
</template>
